import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Card, StatCard } from '../components/UI';
import { analyticsAPI } from '../api/apiClient';
import toast from 'react-hot-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const SubscriptionAnalyticsPage = () => {
  const [summary, setSummary] = useState(null);
  const [revenueChart, setRevenueChart] = useState([]);
  const [growthChart, setGrowthChart] = useState([]);
  const [statusChart, setStatusChart] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscriptionAnalytics();
  }, []);

  const loadSubscriptionAnalytics = async () => {
    try {
      setLoading(true);
      const [summaryRes, revenueRes, growthRes, statusRes] = await Promise.all([
        analyticsAPI.getSubscriptionSummary(),
        analyticsAPI.getSubscriptionRevenue(30),
        analyticsAPI.getSubscriptionGrowth(30),
        analyticsAPI.getDeviceStatusChart(),
      ]);

      setSummary(summaryRes.data.data);
      setRevenueChart(
        Array.isArray(revenueRes.data.data?.data)
          ? revenueRes.data.data.data
          : revenueRes.data.data || []
      );
      setGrowthChart(
        Array.isArray(growthRes.data.data?.data)
          ? growthRes.data.data.data
          : growthRes.data.data || []
      );
      setStatusChart(
        Array.isArray(statusRes.data.data?.data)
          ? statusRes.data.data.data
          : statusRes.data.data || []
      );
    } catch (error) {
      toast.error('Failed to load subscription analytics');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981', '#f59e0b'];

  if (loading) {
    return (
      <div className="text-center py-16 text-gray-400">
        <div className="inline-block w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <BarChart3 size={32} className="text-purple-400" />
          <span>Subscription Analytics</span>
        </h1>
        <p className="text-gray-400 mt-2">Monitor subscription revenue, growth, and device status.</p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <StatCard label="Total Devices" value={summary.totalDevices} icon={ShieldCheck} />
          <StatCard label="Free Devices" value={summary.freeDevices} icon={ShieldAlert} />
          <StatCard label="Premium Devices" value={summary.premiumDevices} icon={TrendingUp} />
          <StatCard label="Blocked Devices" value={summary.blockedDevices} icon={ShieldAlert} />
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <h3 className="text-lg font-bold text-white mb-4">Revenue Overview</h3>
            <div className="space-y-3">
              <div className="bg-gray-900 p-4 rounded-lg">
                <p className="text-gray-400">Today Revenue</p>
                <p className="text-2xl font-bold text-white">{summary.todayRevenue} TK</p>
              </div>
              <div className="bg-gray-900 p-4 rounded-lg">
                <p className="text-gray-400">Monthly Revenue</p>
                <p className="text-2xl font-bold text-white">{summary.monthlyRevenue} TK</p>
              </div>
              <div className="bg-gray-900 p-4 rounded-lg">
                <p className="text-gray-400">Total Revenue</p>
                <p className="text-2xl font-bold text-white">{summary.totalRevenue} TK</p>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-bold text-white mb-4">Request Pipeline</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-gray-300">
                <span>Pending</span>
                <span>{summary.pendingRequests}</span>
              </div>
              <div className="flex items-center justify-between text-gray-300">
                <span>Active Subscriptions</span>
                <span>{summary.activeSubscriptions}</span>
              </div>
              <div className="flex items-center justify-between text-gray-300">
                <span>Expired Subscriptions</span>
                <span>{summary.expiredSubscriptions}</span>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-bold text-white mb-4">Device Status</h3>
            <div className="space-y-3">
              {statusChart.map((item, index) => (
                <div key={item._id} className="flex items-center justify-between text-gray-300">
                  <span>{item._id}</span>
                  <span>{item.count}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-bold text-white mb-4">Subscription Revenue (30 days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="_id" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
              <Line type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <h3 className="text-lg font-bold text-white mb-4">Subscription Growth (30 days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={growthChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="_id" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
              <Bar dataKey="count" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card>
        <h3 className="text-lg font-bold text-white mb-4">Device Status Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={statusChart} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={100} label>
              {statusChart.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

export default SubscriptionAnalyticsPage;
